<template>
    <div class="terms-for-chain">
        <!-- Readonly Mode -->
        <div v-if="!isEditing" class="terms-display">
            <div class="terms-header">
                <h2>{{ $translate('General terms and conditions') }}</h2>
                <v-btn
                    v-if="hasSettingsPermission || hasChainPermission"
                    @click="startEdit"
                    color="primary"
                    text
                    small
                >
                    <v-icon left small>mdi-pencil</v-icon>
                    {{ $translate('Edit') }}
                </v-btn>
            </div>

            <div v-if="globalTerms === null || localTerms === null" class="loading">
                {{ $translate('Loading...') }} <v-progress-circular indeterminate color="primary" size="20" />
            </div>

            <div v-else class="combined-terms">
                <!-- Global Terms -->
                <div class="terms-section" :class="{ 'disabled-section': !hasChainPermission && hasSettingsPermission && isEditing }">
                    <div class="terms-content" v-html="globalTerms" />
                </div>

                <!-- Local Terms -->
                <div class="terms-section local-section">
                    <div class="terms-content" v-html="localTerms" />
                </div>
            </div>
        </div>

        <!-- Edit Mode -->
        <div v-else class="terms-edit-mode">
            <div class="edit-header">
                <h2>{{ $translate('Edit') }}: {{ $translate('General terms and conditions') }}</h2>
                <div class="edit-actions">
                    <v-btn
                        @click="save"
                        color="primary"
                        :loading="saving"
                        :disabled="!hasChanges"
                    >
                        {{ $translate('Save') }}
                    </v-btn>
                    <v-btn @click="cancelEdit" text color="primary">
                        {{ $translate('Cancel') }}
                    </v-btn>
                </div>
            </div>

            <div class="combined-edit">
                <!-- Global Terms Section -->
                <div :class="hasChainPermission ? 'editable-section' : 'readonly-section'">
                    <editor v-if="hasChainPermission" v-model="changedGlobalTerms" :bubble="true" class="terms-editor" />
                    <div v-else>
                        <div v-if="globalTerms === null" class="loading">
                            {{ $translate('Loading...') }} <v-progress-circular indeterminate color="primary" size="20" />
                        </div>
                        <div v-else class="terms-content" v-html="globalTerms" />
                    </div>
                </div>

                <!-- Local Terms Section -->
                <div ref="localSection" :class="hasSettingsPermission ? 'editable-section' : 'readonly-section'">
                    <editor v-if="hasSettingsPermission" v-model="changedLocalTerms" :bubble="true" class="terms-editor" />
                    <div v-else>
                        <div v-if="localTerms === null" class="loading">
                            {{ $translate('Loading...') }} <v-progress-circular indeterminate color="primary" size="20" />
                        </div>
                        <div v-else class="terms-content" v-html="localTerms" />
                    </div>
                </div>
            </div>

            <div class="bottom-actions">
                <v-btn
                    @click="save"
                    color="primary"
                    :loading="saving"
                    :disabled="!hasChanges"
                >
                    {{ $translate('Save') }}
                </v-btn>
                <v-btn @click="cancelEdit" text color="primary">
                    {{ $translate('Cancel') }}
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
import Editor from '../components/Editor.vue'

export default {
    name: 'terms-for-chain',

    zoezi: {
        title: 'Terms for Chain',
        icon: 'mdi-check',
        addon: 'Chain'
    },

    components: {
        Editor
    },

    props: {
        type: {
            type: String,
            default: 'general'
        }
    },

    data: () => ({
        globalTerms: null,
        localTerms: null,
        changedGlobalTerms: null,
        changedLocalTerms: null,
        isEditing: false,
        saving: false
    }),

    computed: {
        hasChainPermission() {
            return this.$store.state?.user?.permissions?.find(x => x == 'Chain settings');
        },
        hasSettingsPermission() {
            return this.$store.state?.user?.permissions?.find(x => x == 'Settings');
        },
        hasChanges() {
            const globalChanged = this.hasChainPermission && this.globalTerms != this.changedGlobalTerms;
            const localChanged = this.hasSettingsPermission && this.localTerms != this.changedLocalTerms;
            return globalChanged || localChanged;
        }
    },

    mounted() {
        this.loadTerms();
    },

    methods: {
        loadTerms() {
            // Load global terms
            this.$api.get('/api/public/terms/get', { type: this.type, is_global: true })
                .then(terms => this.globalTerms = terms);

            // Load local terms
            this.$api.get('/api/public/terms/get', { type: this.type, is_global: false })
                .then(terms => this.localTerms = terms);
        },

        startEdit() {
            this.changedGlobalTerms = this.globalTerms;
            this.changedLocalTerms = this.localTerms;
            this.isEditing = true;

            // Auto-scroll to local section if user can only edit local terms
            if (this.hasSettingsPermission && !this.hasChainPermission) {
                this.$nextTick(() => {
                    if (this.$refs.localSection) {
                        this.$refs.localSection.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                });
            }
        },

        cancelEdit() {
            this.isEditing = false;
            this.changedGlobalTerms = null;
            this.changedLocalTerms = null;
        },

        showError(message) {
            let errorMessage = message || 'The terms could not be saved';
            this.$store.commit('showErrorDialog', { title: this.$translate('Error'), text: this.$translate(errorMessage) });
        },

        async save() {
            this.saving = true;
            const promises = [];

            // Save global terms if user has permission and they changed
            if (this.hasChainPermission && this.globalTerms != this.changedGlobalTerms) {
                promises.push(
                    this.$api.post('/api/terms/change',
                        { text: this.changedGlobalTerms },
                        { type: this.type, is_global: true }
                    ).then((response) => {
                        if (response.ok) {
                            this.globalTerms = this.changedGlobalTerms;
                        } else {
                            this.showError(response.message);
                        }
                    })
                );
            }

            // Save local terms if user has permission and they changed
            if (this.hasSettingsPermission && this.localTerms != this.changedLocalTerms) {
                promises.push(
                    this.$api.post('/api/terms/change',
                        { text: this.changedLocalTerms },
                        { type: this.type, is_global: false }
                    ).then((response) => {
                        if (response.ok) {
                            this.localTerms = this.changedLocalTerms;
                        } else {
                            this.showError(response.message);
                        }
                    })
                );
            }

            try {
                await Promise.all(promises);
                this.isEditing = false;
                this.changedGlobalTerms = null;
                this.changedLocalTerms = null;
            } catch (error) {
                console.error('Error saving terms:', error);
                this.showError('The terms could not be saved');
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>

<style scoped>
.terms-for-chain {
    max-width: 1200px;
    margin: 0 auto;
    padding: 24px;
}

.terms-display {
    background: white;
    border-radius: 4px;
    padding: 24px;
}

.terms-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e0e0e0;
}

.terms-header h2 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 500;
}

.edit-buttons {
    display: flex;
    gap: 8px;
}

.combined-terms {
    line-height: 1.6;
}

.terms-section {
    margin-bottom: 48px;
    transition: all 0.3s ease;
}

.terms-section.disabled-section {
    opacity: 0.5;
    filter: grayscale(50%);
}

.terms-section.local-section {
    margin-bottom: 0;
}

.section-label {
    display: inline-block;
    margin: 0 0 16px 0;
    padding: 4px 12px;
    font-size: 0.875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background-color: #e3f2fd;
    color: #1976d2;
    border-radius: 4px;
}

.section-label.editing {
    background-color: #c8e6c9;
    color: #2e7d32;
}

.terms-content {
    line-height: 1.6;
    color: #333;
}

.terms-content >>> h1,
.terms-content >>> h2,
.terms-content >>> h3 {
    margin-top: 24px;
    margin-bottom: 12px;
}

.terms-content >>> p {
    margin-bottom: 12px;
}

.terms-content >>> ul,
.terms-content >>> ol {
    margin-bottom: 12px;
    padding-left: 24px;
}

.loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 0;
}

.terms-edit-mode {
    background: white;
    border-radius: 4px;
    padding: 24px;
}

.edit-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    padding-bottom: 16px;
    border-bottom: 2px solid #e0e0e0;
}

.edit-header h2 {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 500;
}

.edit-actions {
    display: flex;
    gap: 12px;
}

.combined-edit {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.editable-section {
    flex: 1;
}

.readonly-section {
    flex: 1;
    opacity: 0.4;
    pointer-events: none;
    filter: grayscale(70%);
    background-color: #fafafa;
    padding: 16px;
    border-radius: 4px;
    border-left: 4px solid #e0e0e0;
}

.terms-editor {
    margin-top: 8px;
    min-height: 300px;
}

.terms-editor >>> .ProseMirror {
    min-height: 300px;
    padding: 16px;
    border: 2px solid #e0e0e0;
    border-radius: 4px;
    background: white;
    transition: border-color 0.2s ease;
}

.terms-editor >>> .ProseMirror:focus {
    outline: none;
    border-color: #1976d2;
    box-shadow: 0 0 0 1px rgba(25, 118, 210, 0.2);
}

.bottom-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 2px solid #e0e0e0;
}
</style>
